import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, QA } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

export const JSModules: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Modules</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    Self-contained units of code that <code>export</code> what others need and <code>import</code> what they depend on — the system that turned JavaScript from global scripts into scalable applications.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="16 min read" />
  </div>

  {/* 1. Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>An app starts as one <Code>app.js</Code>, then grows into users, orders, payments, inventory, notifications… Before ES6, every file was loaded globally:</Para>
   <CodeBlock snippets={[{ language: 'html', code: `<script src="users.js"></script>\n<script src="orders.js"></script>\n<script src="payments.js"></script>` }]} defaultLanguage="html" id="mod-scripts" />
   <Para>Everything shared one global scope, so names collided silently:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `// users.js\nvar name = "Sachin";\n\n// orders.js\nvar name = "Order Service"; // which one wins? nobody knows` }]} defaultLanguage="javascript" id="mod-collision" />
   <Para>Global pollution, naming collisions, tangled dependencies, hard testing — JavaScript needed a way to organize code into independent units. That&apos;s modules.</Para>
  </section>

  {/* 2. Definition */}
  <section className="mb-12">
   <SectionHeading n={2}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200">A <Mark>module</Mark> is a self-contained unit of code that encapsulates functionality and exposes only what other modules need — a small independent package.</p>
   </div>
   <CodeBlock snippets={[{ language: 'javascript', code: `// math.js\nexport function add(a, b) {\n  return a + b;\n}\n\n// app.js\nimport { add } from "./math.js";` }]} defaultLanguage="javascript" id="mod-definition" />
   <div className="flex flex-wrap gap-2">{['Encapsulation', 'Reusability', 'Maintainability', 'Scalability'].map((b) => <Pill key={b}>{b}</Pill>)}</div>
  </section>

  {/* 3. Why */}
  <section className="mb-12">
   <SectionHeading n={3}>Why Modules Exist</SectionHeading>
   <Para>Without modules, everything is visible to everyone — one giant shared hall. With modules, each unit owns its code, like rooms in a house. Each module exposes a small surface and hides the rest.</Para>
  </section>

  {/* 4. Core idea */}
  <section className="mb-12">
   <SectionHeading n={4}>Core Idea — Export &amp; Import</SectionHeading>
   <Para>A module has two jobs: <Mark>export</Mark> (expose functionality) and <Mark>import</Mark> (consume it).</Para>
   <pre className="text-[13px] font-mono text-gray-600 dark:text-gray-400">{`Module A  ──exports──►  Module B (imports)`}</pre>
  </section>

  {/* 5. Before ES6 */}
  <section className="mb-12">
   <SectionHeading n={5}>Before ES6 Modules</SectionHeading>
   <Para>JavaScript had no official module system, so developers faked private scope with an IIFE:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `(function () {\n  const secret = "hidden"; // private to this IIFE\n})();` }]} defaultLanguage="javascript" id="mod-iife" />
   <Para>It worked but was messy. The ecosystem needed a standard — which ES6 delivered as <Mark>ES Modules (ESM)</Mark> with <Code>export</Code> and <Code>import</Code>.</Para>
  </section>

  {/* 6. Named exports */}
  <section className="mb-12">
   <SectionHeading n={6}>Named Exports</SectionHeading>
   <Para>Export as many named members as you like, and import them by name:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `// math.js\nexport const PI = 3.14;\nexport function add(a, b) { return a + b; }\nexport class User {}\n\n// app.js\nimport { PI, add, User } from "./math.js";\nadd(10, 20); // 30` }]} defaultLanguage="javascript" id="mod-named" />
   <Para>Variables, functions, classes, objects, constants — all can be named-exported.</Para>
  </section>

  {/* 7. Default exports */}
  <section className="mb-12">
   <SectionHeading n={7}>Default Exports</SectionHeading>
   <Para>When a module has one primary thing, use a default export — imported <Mark>without</Mark> curly braces and with any name:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `// User.js\nexport default class User {}\n\n// app.js\nimport User from "./User.js"; // no braces` }]} defaultLanguage="javascript" id="mod-default" />
  </section>

  {/* 8. Named vs default */}
  <section className="mb-12">
   <SectionHeading n={8}>Named vs Default Export</SectionHeading>
   <div className="my-6 overflow-x-auto">
    <table className="w-full text-sm">
     <thead>
      <tr className="border-b border-gray-200 dark:border-gray-700">
       {['Feature', 'Named export', 'Default export'].map((h) => <th key={h} className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">{h}</th>)}
      </tr>
     </thead>
     <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
      {[
       ['Multiple per file', 'Yes', 'No (one)'],
       ['Curly braces on import', 'Yes', 'No'],
       ['Name must match', 'Yes', 'No (any name)'],
       ['Best for', 'Utilities, multiple helpers', 'The module\u2019s main thing'],
      ].map((r) => (
       <tr key={r[0]}>
        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{r[0]}</td>
        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{r[1]}</td>
        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{r[2]}</td>
       </tr>
      ))}
     </tbody>
    </table>
   </div>
  </section>

  {/* 9. Aliasing + re-export */}
  <section className="mb-12">
   <SectionHeading n={9}>Import Aliasing &amp; Barrel Re-exports</SectionHeading>
   <Para>Rename on import to avoid conflicts, and aggregate many modules behind one entry point (a &quot;barrel&quot;):</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `// aliasing\nimport { User as AppUser } from "./User.js";\nnew AppUser();\n\n// index.js — barrel file\nexport * from "./user.js";\nexport * from "./order.js";\n// consumers: import { User, Order } from "./index.js";` }]} defaultLanguage="javascript" id="mod-barrel" />
   <Callout tone="info">Barrel files are common in React, NestJS, and Angular to give a clean single import path per feature.</Callout>
  </section>

  {/* 10. Module scope */}
  <section className="mb-12">
   <SectionHeading n={10}>Module Scope (Encapsulation)</SectionHeading>
   <Para>Anything not exported is private to the module — encapsulation at the file level:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `// payment.js\nfunction validateCard() {}   // private\nfunction chargeCard() {}     // exposed below\n\nexport { chargeCard };\n// outside code can call chargeCard(), never validateCard()` }]} defaultLanguage="javascript" id="mod-scope" />
  </section>

  {/* 11. Loading + caching */}
  <section className="mb-12">
   <SectionHeading n={11}>Module Loading &amp; Caching</SectionHeading>
   <Para>On import, JS loads → evaluates → <Mark>caches</Mark> → returns the exports. A module&apos;s top-level code runs <Mark>only once</Mark>, no matter how many files import it:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `// config.js\nconsole.log("Loaded");\n\n// imported in many files, but prints "Loaded" exactly once` }]} defaultLanguage="javascript" id="mod-cache" />
   <Output>Loaded</Output>
   <Callout tone="info">This caching is why importing a module everywhere is cheap — it&apos;s a performance and memory win, and a frequent interview point.</Callout>
  </section>

  {/* 12. Dynamic imports */}
  <section className="mb-12">
   <SectionHeading n={12}>Dynamic Imports (Lazy Loading)</SectionHeading>
   <Para>Load a module only when it&apos;s actually needed — the basis of code-splitting:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const module = await import("./payment.js");\nmodule.chargeCard();` }]} defaultLanguage="javascript" id="mod-dynamic" />
   <div className="flex flex-wrap gap-2">{['Smaller initial bundle', 'Faster startup', 'Better performance'].map((b) => <Pill key={b}>{b}</Pill>)}</div>
   <Para>Heavily used in React (<Code>React.lazy</Code>), Next.js, and micro-frontends.</Para>
  </section>

  {/* 13. Browser + Node */}
  <section className="mb-12">
   <SectionHeading n={13}>Browser &amp; Node.js</SectionHeading>
   <Para>Browsers load ESM via <Code>{`<script type="module">`}</Code>. Node.js historically used CommonJS but now supports ESM too:</Para>
   <CodeBlock snippets={[{ language: 'html', code: `<script type="module" src="app.js"></script>` }]} defaultLanguage="html" id="mod-browser" />
  </section>

  {/* 14. CommonJS vs ESM */}
  <section className="mb-12">
   <SectionHeading n={14}>CommonJS vs ES Modules</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `// CommonJS (Node legacy)\nconst math = require("./math");\nmodule.exports = { add };\n\n// ES Modules (standard)\nimport { add } from "./math.js";\nexport function add() {}` }]} defaultLanguage="javascript" id="mod-cjs" />
   <div className="my-6 overflow-x-auto">
    <table className="w-full text-sm">
     <thead>
      <tr className="border-b border-gray-200 dark:border-gray-700">
       {['Feature', 'CommonJS', 'ES Modules'].map((h) => <th key={h} className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">{h}</th>)}
      </tr>
     </thead>
     <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
      {[
       ['Import', 'require()', 'import'],
       ['Export', 'module.exports', 'export'],
       ['Loading', 'Runtime (dynamic)', 'Static (parsed first)'],
       ['Status', 'Node legacy', 'Official standard'],
      ].map((r) => (
       <tr key={r[0]}>
        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{r[0]}</td>
        <td className="px-4 py-3 font-mono text-gray-700 dark:text-gray-300">{r[1]}</td>
        <td className="px-4 py-3 font-mono text-gray-700 dark:text-gray-300">{r[2]}</td>
       </tr>
      ))}
     </tbody>
    </table>
   </div>
   <Callout tone="info">Because ESM is <Mark>static</Mark>, tools can analyze imports ahead of time — enabling tree-shaking (dropping unused exports).</Callout>
  </section>

  {/* 15. Across the ecosystem */}
  <section className="mb-12">
   <SectionHeading n={15}>Across the Ecosystem</SectionHeading>
   <CodeBlock snippets={[{ language: 'typescript', code: `// TypeScript\nexport interface User { id: number; name: string; }\nimport { User } from "./User";` }]} defaultLanguage="typescript" id="mod-ts" />
   <CodeBlock snippets={[{ language: 'javascript', code: `// React\nexport default function Header() {}\nimport Header from "./Header";` }]} defaultLanguage="javascript" id="mod-react" />
   <CodeBlock snippets={[{ language: 'typescript', code: `// NestJS\nexport class UserService {}\nimport { UserService } from "./user.service";` }]} defaultLanguage="typescript" id="mod-nest" />
  </section>

  {/* 16. Common mistakes */}
  <section className="mb-12">
   <SectionHeading n={16}>Common Mistakes</SectionHeading>
   <Callout tone="warning" title="Default vs named confusion">Default imports take no braces; named imports require them and must match the exported name.</Callout>
   <Callout tone="warning" title="Exporting everything">Expose only the public surface — keep internals private to the module.</Callout>
   <Callout tone="warning" title="Circular dependencies"><Code>A imports B</Code> and <Code>B imports A</Code> can yield <Code>undefined</Code> exports at load time — restructure shared code into a third module.</Callout>
   <Callout tone="warning" title="Mixing CommonJS and ESM">Inconsistent <Code>require</Code>/<Code>import</Code> in the same project causes interop headaches — pick one per package.</Callout>
  </section>

  {/* 17. Real interview questions */}
  <section className="mb-12">
   <SectionHeading n={17}>Real Interview Questions</SectionHeading>
   <QA q="What problem do modules solve?">
    Global-scope pollution and naming collisions. They give each file its own scope and an explicit export/import contract for dependencies.
   </QA>
   <QA q="Difference between named and default exports?">
    A file can have many named exports (imported with braces, matching names) but only one default export (imported without braces, any name).
   </QA>
   <QA q="What is module caching?">
    A module&apos;s top-level code executes once; the result is cached and reused for every later import — a performance and memory optimization.
   </QA>
   <QA q="CommonJS vs ES Modules?">
    CommonJS uses <Code>require</Code>/<Code>module.exports</Code> and loads at runtime (Node legacy). ESM uses <Code>import</Code>/<Code>export</Code>, loads statically, and is the official standard (enables tree-shaking).
   </QA>
   <QA q="How does dynamic import work and why use it?">
    <Code>await import("./x.js")</Code> loads a module on demand, returning a promise — used for code-splitting to shrink the initial bundle.
   </QA>
   <QA q="What is module scope?">
    Everything declared in a module is private unless explicitly exported — file-level encapsulation.
   </QA>
  </section>

  {/* 18. Mental model */}
  <section className="mb-12">
   <SectionHeading n={18}>Mental Model</SectionHeading>
   <Para>Think of a company with departments — HR, Finance, Engineering. Each has internal processes and exposes only what others need. Modules work exactly the same way: private internals, a small public interface.</Para>
  </section>

  {/* 19 & 20 */}
  <section className="mb-4">
   <SectionHeading n={19}>Interview One-Liner &amp; Takeaway</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <div className="text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-2">Interview One-Liner</div>
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     Modules are self-contained units of code that improve organization, encapsulation, and reuse by exporting functionality and importing dependencies through a standard system.
    </p>
   </div>
   <Callout tone="info" title="Most important takeaway">
    Modules turned JavaScript from global scripts into a scalable application language — providing encapsulation, dependency management, and code reuse. Understand exports, imports, module scope, caching, and CommonJS vs ESM deeply for interviews.
   </Callout>
  </section>
 </div>
);

export default JSModules;
